import {
  FieldWrapper,
  SelectionListChangeEvent,
  SelectProps,
} from '@dixa/lib-shared-habla';
import type { Tag } from 'lib-shared-types-api';
import { useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import { TagsDropdownButton } from 'components/conversation/conversationTags/components/tagsDropdownButton/tagsDropdownButton';
import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'utils/hookForm';

/**
 * Represents the React Hook Form variant of the {@link TagsDropdownButton}
 * component.
 */
export const HookFormCategorizedTagsDropdown = <
  TItemType extends Tag,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    SelectProps<TItemType>
  >,
): JSX.Element => {
  const { controller, controllerProps, fieldWrapperProps } =
    useHookFormFieldWrapper(props);

  const [selectedItems, setSelectedItems] = useState<Tag[]>(
    ((controllerProps.defaultValue ??
      props.componentProps.selectedItems) as Tag[]) ?? [],
  );

  // Handles calling React Hook Form change event because the select component
  // doesn't conform to what.
  useUpdateEffect(() => {
    props.componentProps.onSelect?.(selectedItems);
    props.componentProps.onChange?.(selectedItems);
    controller.field.onChange?.(selectedItems);
  }, [controller.field.onChange, selectedItems]);

  const handleChange = ({
    selectedItem: { isSelected, value: tag },
  }: SelectionListChangeEvent<Tag>) => {
    setSelectedItems(
      isSelected
        ? selectedItems.concat(tag)
        : selectedItems.filter(({ id }) => id !== tag.id),
    );
  };

  return (
    <FieldWrapper {...fieldWrapperProps}>
      <TagsDropdownButton
        tags={selectedItems}
        onChange={handleChange}
        isLoading={false}
      />
    </FieldWrapper>
  );
};
